import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    Check,
    Loader2,
    CreditCard,
    PartyPopper,
    ShieldCheck,
    ArrowRight,
    ArrowLeft,
    UploadCloud,
    FileText,
    X,
    Building2,
    Target,
    Download,
    Mail,
    Tag,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    initDiagnostic,
    verifyDiagnostic,
    uploadDiagnosticFile,
    submitDiagnostic,
    getDiagnosticStatus,
    adminCreateManual,
    adminSendReport,
    validateCoupon,
    apiErr,
    API,
} from "@/lib/api";
import { DIAGNOSTIC_PRICE } from "@/lib/config";

const STEPS = ["Pay", "Business", "Goals", "Files", "Review"];

const EMPLOYEES = ["Only me", "2–10", "10–20", "20–50", "50+"];
const BUSINESS_TYPES = ["B2C", "B2B", "D2C", "B2B + B2C", "Service business", "Product business"];
const REVENUE = ["Just starting", "Under ₹1 Lakh", "₹1–5 Lakh", "₹5–20 Lakh", "₹20–50 Lakh", "₹50 Lakh–₹1 Crore", "₹1 Crore+"];
const GROWTH = ["5%", "10%", "20%", "30%", "50%", "More than 50%"];
const WEBSITE_AGE = ["No website", "Less than 1 year", "1–2 years", "3–5 years", "5–8 years", "8–10 years", "10+ years"];

const UPLOAD_SECTIONS = [
    { key: "logo", title: "Logo files", text: "Upload your logo files", accept: "image/png,image/jpeg,application/pdf", types: "JPG, PNG, PDF", limit: 3, hint: "1–3 files · recommended" },
    { key: "profile", title: "Company profile or brochure", text: "Upload your company profile, brochure or presentation", accept: "application/pdf,image/jpeg,image/png", types: "PDF, JPG, PNG", limit: 3, hint: "1–3 files · optional" },
    { key: "products", title: "Product or service images", text: "Upload your product images or service-related visuals", accept: "image/jpeg,image/png", types: "JPG, PNG", limit: 10, hint: "1–10 files · optional" },
    { key: "location", title: "Store, shop, office or location images", text: "Upload your store, shop, office, factory, clinic or location photos", accept: "image/jpeg,image/png", types: "JPG, PNG", limit: 10, hint: "1–10 files · optional" },
    { key: "marketing", title: "Marketing screenshots", text: "Upload any social media, ad, website or marketing screenshots if available", accept: "image/jpeg,image/png,application/pdf", types: "JPG, PNG, PDF", limit: 5, hint: "1–5 files · optional" },
];

function loadRazorpay() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

const emptyDetails = {
    full_name: "",
    email: "",
    mobile: "",
    business_name: "",
    business_category: "",
    website: "",
    google_map_link: "",
    instagram_link: "",
    linkedin_link: "",
    facebook_link: "",
    full_address: "",
    gst_number: "",
    employee_count: "",
    business_type: "",
    competitor_1: "",
    competitor_1_website: "",
    competitor_2: "",
    competitor_2_website: "",
    competitor_3: "",
    competitor_3_website: "",
    monthly_revenue: "",
    growth_target: "",
    main_problem: "",
    support_needed: "",
    social_media_mgmt: "",
    digital_marketing: "",
    worked_with_agency_before: "",
    currently_with_agency: "",
    website_age: "",
    has_mobile_app: "",
    selling_online: "",
    margin_percentage: "",
    niche_audience: "",
    attended_events: "",
    received_awards: "",
    pr_activity: "",
};

const labelCls = "text-xs uppercase tracking-[0.2em] text-white/55";

function Chips({ value, options, onChange, testid }) {
    return (
        <div className="mt-2 flex flex-wrap gap-2" data-testid={testid}>
            {options.map((o) => (
                <button
                    key={o}
                    type="button"
                    data-testid={`${testid}-${o}`}
                    onClick={() => onChange(o)}
                    className={`rounded-full px-4 py-2 text-xs transition ${value === o ? "bg-gold-300 text-black" : "glass glass-hover text-white/70"}`}
                >
                    {o}
                </button>
            ))}
        </div>
    );
}

function YesNo({ value, onChange, testid }) {
    return (
        <div className="mt-2 flex gap-2" data-testid={testid}>
            {["Yes", "No"].map((o) => (
                <button
                    key={o}
                    type="button"
                    data-testid={`${testid}-${o.toLowerCase()}`}
                    onClick={() => onChange(o)}
                    className={`rounded-full px-6 py-2 text-xs transition ${value === o ? "bg-gold-300 text-black" : "glass glass-hover text-white/70"}`}
                >
                    {o}
                </button>
            ))}
        </div>
    );
}

function QuestionRow({ q, value, onChange, testid }) {
    return (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md glass px-4 py-3">
            <span className="text-sm text-white/80">{q}</span>
            <YesNo value={value} onChange={onChange} testid={testid} />
        </div>
    );
}

export default function DiagnosticFlow({ adminMode = false, token = null } = {}) {
    const [step, setStep] = useState(0);
    const [contact, setContact] = useState({ full_name: "", email: "", mobile: "" });
    const [paying, setPaying] = useState(false);
    const [diagnosticId, setDiagnosticId] = useState(null);
    const [details, setDetails] = useState(emptyDetails);
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [reportReady, setReportReady] = useState(false);
    const [reportFailed, setReportFailed] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [couponInput, setCouponInput] = useState("");
    const [coupon, setCoupon] = useState(null);
    const [couponMsg, setCouponMsg] = useState(null);
    const [applyingCoupon, setApplyingCoupon] = useState(false);
    const fileRefs = useRef({});

    const applyCoupon = async () => {
        if (!couponInput.trim()) return;
        setApplyingCoupon(true);
        try {
            const res = await validateCoupon(couponInput.trim().toUpperCase(), "report", contact.email);
            if (res.valid) { setCoupon(res); setCouponMsg({ ok: true, text: `Coupon applied — ₹${res.discount} off` }); }
            else { setCoupon(null); setCouponMsg({ ok: false, text: res.message || "Invalid coupon" }); }
        } catch { setCouponMsg({ ok: false, text: "Could not validate coupon" }); }
        finally { setApplyingCoupon(false); }
    };

    useEffect(() => {
        if (!adminMode) loadRazorpay();
    }, [adminMode]);

    // Poll for report readiness on the final step
    useEffect(() => {
        if (step !== 5 || !diagnosticId || reportReady || reportFailed) return;
        let active = true;
        const tick = async () => {
            try {
                const s = await getDiagnosticStatus(diagnosticId);
                if (active && s.report_ready) {
                    setReportReady(true);
                    return;
                }
                if (active && s.analysis_status === "failed") {
                    setReportFailed(true);
                    return;
                }
            } catch {
                /* keep polling */
            }
            if (active) timer = setTimeout(tick, 4000);
        };
        let timer = setTimeout(tick, 3000);
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [step, diagnosticId, reportReady, reportFailed]);

    const downloadReport = async () => {
        setDownloading(true);
        try {
            const resp = await fetch(
                adminMode
                    ? `${API}/admin/diagnostics/${diagnosticId}/report.pdf`
                    : `${API}/diagnostic/${diagnosticId}/report.pdf`,
                adminMode ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
            );
            if (!resp.ok) throw new Error("not ready");
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "Business_Diagnostic_Report.pdf";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Report is still being prepared. Please try again in a moment.");
        } finally {
            setDownloading(false);
        }
    };

    const sendEmail = async () => {
        setSendingEmail(true);
        try {
            const res = await adminSendReport(diagnosticId, token);
            toast.success(`Report emailed to ${res.sent_to}`);
        } catch (e) {
            toast.error(apiErr(e, "Could not send email. Is RESEND_API_KEY configured?"));
        } finally {
            setSendingEmail(false);
        }
    };

    const inputCls =
        "mt-2 h-12 border-white/10 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-gold-300/40 focus-visible:border-gold-300/50";
    const areaCls = "mt-2 border-white/10 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-gold-300/40";
    const setD = (k) => (e) => setDetails((d) => ({ ...d, [k]: e.target.value }));
    const setVal = (k, v) => setDetails((d) => ({ ...d, [k]: v }));

    const startPayment = async () => {
        if (!contact.full_name || !contact.email || !contact.mobile) {
            toast.error("Please fill your name, email and mobile.");
            return;
        }
        if (adminMode) {
            setPaying(true);
            try {
                const res = await adminCreateManual(contact, token);
                setDiagnosticId(res.diagnostic_id);
                setDetails((d) => ({ ...d, ...contact }));
                setStep(1);
            } catch (e) {
                toast.error(apiErr(e, "Could not start analysis."));
            } finally {
                setPaying(false);
            }
            return;
        }
        setPaying(true);
        try {
            const ok = await loadRazorpay();
            if (!ok) throw new Error("Razorpay failed to load");
            const order = await initDiagnostic({ ...contact, coupon_code: coupon?.code || null });
            setDiagnosticId(order.booking_id);
            setDetails((d) => ({ ...d, ...contact }));

            const rzp = new window.Razorpay({
                key: order.razorpay_key_id,
                amount: order.amount * 100,
                currency: order.currency,
                name: "Abhijit JK",
                description: "Business Diagnostic Report",
                order_id: order.razorpay_order_id,
                prefill: {
                    name: contact.full_name,
                    email: contact.email,
                    contact: contact.mobile,
                },
                theme: { color: "#D4AF37" },
                handler: async (resp) => {
                    try {
                        await verifyDiagnostic({
                            booking_id: order.booking_id,
                            razorpay_payment_id: resp.razorpay_payment_id,
                            razorpay_order_id: resp.razorpay_order_id,
                            razorpay_signature: resp.razorpay_signature,
                        });
                        toast.success("Payment successful!");
                        setStep(1);
                    } catch {
                        toast.error("Payment verification failed. Contact support.");
                    } finally {
                        setPaying(false);
                    }
                },
                modal: { ondismiss: () => setPaying(false) },
            });
            rzp.on("payment.failed", () => {
                toast.error("Payment failed. Please try again.");
                setPaying(false);
            });
            rzp.open();
        } catch (e) {
            toast.error(apiErr(e, "Could not start payment. Please try again or reach us on WhatsApp."));
            setPaying(false);
        }
    };

    const onPickFiles = (category, limit) => async (e) => {
        const picked = Array.from(e.target.files || []);
        if (!picked.length) return;
        const existing = files.filter((f) => f.category === category).length;
        if (existing + picked.length > limit) {
            toast.error(`You can upload up to ${limit} file(s) here.`);
            if (fileRefs.current[category]) fileRefs.current[category].value = "";
            return;
        }
        setUploading(category);
        for (const f of picked) {
            if (f.size > 15 * 1024 * 1024) {
                toast.error(`${f.name} is over 15MB and was skipped.`);
                continue;
            }
            try {
                const fd = new FormData();
                fd.append("file", f);
                fd.append("category", category);
                const res = await uploadDiagnosticFile(diagnosticId, fd);
                setFiles((prev) => [...prev, { ...res, category }]);
            } catch (err) {
                toast.error(apiErr(err, `Could not upload ${f.name}`));
            }
        }
        setUploading(null);
        if (fileRefs.current[category]) fileRefs.current[category].value = "";
    };

    const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

    const validateBusiness = () => {
        if (!details.business_name || !details.business_category) {
            toast.error("Please add your business name and category.");
            return false;
        }
        if (!details.website && !details.instagram_link) {
            toast.error("Please add your website or Instagram link.");
            return false;
        }
        return true;
    };

    const validateGoals = () => {
        if (!details.monthly_revenue) {
            toast.error("Please select your monthly revenue range.");
            return false;
        }
        if (!details.main_problem) {
            toast.error("Please describe your main business problem.");
            return false;
        }
        return true;
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            await submitDiagnostic({
                diagnostic_id: diagnosticId,
                details,
                file_ids: files.map((f) => f.id),
            });
            setStep(5);
        } catch (e) {
            toast.error(apiErr(e, "Could not submit. Please contact us on WhatsApp."));
        } finally {
            setSubmitting(false);
        }
    };

    const reviewGroups = [
        {
            title: "Client details",
            rows: [
                ["Full name", details.full_name],
                ["Mobile", details.mobile],
                ["Email", details.email],
            ],
        },
        {
            title: "Business basics",
            rows: [
                ["Business name", details.business_name],
                ["Category", details.business_category],
                ["Full address", details.full_address],
                ["GST number", details.gst_number],
                ["Employees", details.employee_count],
                ["Business type", details.business_type],
            ],
        },
        {
            title: "Online links",
            rows: [
                ["Website", details.website],
                ["Google Map", details.google_map_link],
                ["Instagram", details.instagram_link],
                ["LinkedIn", details.linkedin_link],
                ["Facebook", details.facebook_link],
            ],
        },
        {
            title: "Competitor details",
            rows: [
                ["Competitor 1", [details.competitor_1, details.competitor_1_website].filter(Boolean).join(" · ")],
                ["Competitor 2", [details.competitor_2, details.competitor_2_website].filter(Boolean).join(" · ")],
                ["Competitor 3", [details.competitor_3, details.competitor_3_website].filter(Boolean).join(" · ")],
            ],
        },
        {
            title: "Numbers and goals",
            rows: [
                ["Monthly revenue", details.monthly_revenue],
                ["Growth target", details.growth_target],
                ["Main problem", details.main_problem],
                ["Support needed", details.support_needed],
                ["Website age", details.website_age],
                ["Margin %", details.margin_percentage],
                ["Niche / audience", details.niche_audience],
            ],
        },
        {
            title: "Marketing questions",
            rows: [
                ["Social media management", details.social_media_mgmt],
                ["Digital marketing / ads", details.digital_marketing],
                ["Worked with agency before", details.worked_with_agency_before],
                ["Currently with an agency", details.currently_with_agency],
                ["Mobile app", details.has_mobile_app],
                ["Selling online", details.selling_online],
                ["Attended events", details.attended_events],
                ["Awards / recognition", details.received_awards],
                ["PR / media coverage", details.pr_activity],
            ],
        },
    ];

    return (
        <div data-testid="diagnostic-flow">
            {/* STEP INDICATOR */}
            <div className="mb-10 flex items-center justify-between">
                {STEPS.map((s, i) => (
                    <div key={s} className="flex flex-1 items-center">
                        <div
                            className={`flex h-9 w-9 flex-none items-center justify-center rounded-full text-sm font-medium transition ${
                                i < step
                                    ? "bg-gold-300 text-black"
                                    : i === step
                                    ? "glass glow-border gold-text"
                                    : "border border-white/10 text-white/40"
                            }`}
                        >
                            {i < step ? <Check size={15} /> : i + 1}
                        </div>
                        {i < STEPS.length - 1 && (
                            <div className={`mx-2 h-px flex-1 ${i < step ? "bg-gold-300/60" : "bg-white/10"}`} />
                        )}
                    </div>
                ))}
            </div>

            <AnimatePresence mode="wait">
                {/* STEP 0 — CLIENT DETAILS / PAYMENT (unchanged) */}
                {step === 0 && (
                    <motion.div key="pay" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                        <h3 className="flex items-center gap-2 font-display text-2xl font-medium">
                            <CreditCard size={20} className="text-gold-300" /> {adminMode ? "Client details" : "Secure your report"}
                        </h3>
                        {!adminMode && (
                            <div className="mt-4 glass rounded-md p-5 text-sm text-white/70">
                                <div className="flex flex-wrap items-center gap-4">
                                    <span className="inline-flex items-center gap-2"><FileText size={14} className="text-gold-300" /> Business Diagnostic Report</span>
                                    <span className="ml-auto text-right">
                                        {coupon && <span className="mr-2 text-sm text-white/40 line-through">{DIAGNOSTIC_PRICE}</span>}
                                        <span className="gold-text font-display text-xl font-semibold">₹{(coupon ? coupon.final : 999).toLocaleString("en-IN")}</span>
                                    </span>
                                </div>
                                <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-white/10 pt-4">
                                    <input value={couponInput} onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCoupon(null); setCouponMsg(null); }}
                                        placeholder="Coupon code" data-testid="diag-coupon-input"
                                        className="flex-1 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm uppercase tracking-wide text-white placeholder:normal-case placeholder:text-white/30" />
                                    <button type="button" onClick={applyCoupon} disabled={applyingCoupon || !couponInput.trim()} data-testid="diag-coupon-apply"
                                        className="ghost-button inline-flex items-center gap-2 text-sm disabled:opacity-50">
                                        {applyingCoupon ? <Loader2 size={13} className="animate-spin" /> : <Tag size={13} />} Apply
                                    </button>
                                    {couponMsg && <p data-testid="diag-coupon-msg" className={`w-full text-xs ${couponMsg.ok ? "text-emerald-300" : "text-red-300"}`}>{couponMsg.text}</p>}
                                </div>
                            </div>
                        )}
                        {adminMode && (
                            <p className="mt-2 text-sm text-white/55">Manual analysis — no payment required. Enter the client's details to begin.</p>
                        )}

                        <div className="mt-6 grid gap-5 sm:grid-cols-2">
                            <div>
                                <Label className={labelCls}>Full Name</Label>
                                <Input data-testid="diag-name" value={contact.full_name} onChange={(e) => setContact({ ...contact, full_name: e.target.value })} placeholder="Client name" className={inputCls} />
                            </div>
                            <div>
                                <Label className={labelCls}>Mobile</Label>
                                <Input data-testid="diag-mobile" value={contact.mobile} onChange={(e) => setContact({ ...contact, mobile: e.target.value })} placeholder="+91…" className={inputCls} />
                            </div>
                            <div className="sm:col-span-2">
                                <Label className={labelCls}>Email</Label>
                                <Input type="email" data-testid="diag-email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="client@business.com" className={inputCls} />
                            </div>
                        </div>

                        <button onClick={startPayment} disabled={paying} data-testid="diag-pay-now" className="gold-button mt-8 inline-flex w-full items-center justify-center gap-2 disabled:opacity-50 sm:w-auto">
                            {paying ? <Loader2 className="animate-spin" size={14} /> : adminMode ? <ArrowRight size={14} /> : <CreditCard size={14} />}
                            {adminMode ? "Start Analysis (No Payment)" : `Pay ₹${(coupon ? coupon.final : 999).toLocaleString("en-IN")} Securely`}
                        </button>
                        {!adminMode && (
                            <p className="mt-4 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-white/45">
                                <ShieldCheck size={12} /> Razorpay · UPI, Card, Net Banking
                            </p>
                        )}
                    </motion.div>
                )}

                {/* STEP 1 — BUSINESS BASICS */}
                {step === 1 && (
                    <motion.div key="biz" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                        <h3 className="flex items-center gap-2 font-display text-2xl font-medium">
                            <Building2 size={20} className="text-gold-300" /> Business basics
                        </h3>
                        <p className="mt-2 text-sm text-white/55">Tell me about your business and your market.</p>

                        <div className="mt-7 grid gap-5 sm:grid-cols-2">
                            <div><Label className={labelCls}>Business Name</Label><Input data-testid="diag-business-name" value={details.business_name} onChange={setD("business_name")} placeholder="Business name" className={inputCls} /></div>
                            <div><Label className={labelCls}>Business Category</Label><Input data-testid="diag-category" value={details.business_category} onChange={setD("business_category")} placeholder="e.g. Food, Retail, Clinic, Agency, Manufacturing" className={inputCls} /></div>
                            <div><Label className={labelCls}>Website Address</Label><Input type="url" data-testid="diag-website" value={details.website} onChange={setD("website")} placeholder="Website link" className={inputCls} /></div>
                            <div><Label className={labelCls}>Google Map Link</Label><Input type="url" data-testid="diag-map" value={details.google_map_link} onChange={setD("google_map_link")} placeholder="Google Maps link" className={inputCls} /></div>
                            <div><Label className={labelCls}>Instagram Link</Label><Input type="url" data-testid="diag-instagram" value={details.instagram_link} onChange={setD("instagram_link")} placeholder="Instagram profile link" className={inputCls} /></div>
                            <div><Label className={labelCls}>LinkedIn Link</Label><Input type="url" data-testid="diag-linkedin" value={details.linkedin_link} onChange={setD("linkedin_link")} placeholder="LinkedIn page/profile link" className={inputCls} /></div>
                            <div><Label className={labelCls}>Facebook Link</Label><Input type="url" data-testid="diag-facebook" value={details.facebook_link} onChange={setD("facebook_link")} placeholder="Facebook page link" className={inputCls} /></div>
                            <div><Label className={labelCls}>GST Number <span className="text-white/30">(optional)</span></Label><Input data-testid="diag-gst" value={details.gst_number} onChange={setD("gst_number")} placeholder="GST number if available" className={inputCls} /></div>
                            <div className="sm:col-span-2"><Label className={labelCls}>Full Business Address</Label><Textarea rows={2} data-testid="diag-address" value={details.full_address} onChange={setD("full_address")} placeholder="Full address with city, area and pincode" className={areaCls} /></div>
                        </div>

                        <div className="mt-6">
                            <Label className={labelCls}>Number of Employees</Label>
                            <Chips value={details.employee_count} options={EMPLOYEES} onChange={(v) => setVal("employee_count", v)} testid="diag-employees" />
                        </div>
                        <div className="mt-6">
                            <Label className={labelCls}>Business Type</Label>
                            <Chips value={details.business_type} options={BUSINESS_TYPES} onChange={(v) => setVal("business_type", v)} testid="diag-biztype" />
                        </div>

                        <div className="mt-8">
                            <p className="label-eyebrow">Your top 3 competitors <span className="normal-case tracking-normal text-white/35">(name + website helps the analysis)</span></p>
                            <div className="mt-4 grid gap-5 sm:grid-cols-2">
                                <div><Label className={labelCls}>Competitor 1 name</Label><Input data-testid="diag-comp1" value={details.competitor_1} onChange={setD("competitor_1")} placeholder="Competitor name" className={inputCls} /></div>
                                <div><Label className={labelCls}>Competitor 1 website</Label><Input type="url" data-testid="diag-comp1-web" value={details.competitor_1_website} onChange={setD("competitor_1_website")} placeholder="Competitor website link" className={inputCls} /></div>
                                <div><Label className={labelCls}>Competitor 2 name</Label><Input data-testid="diag-comp2" value={details.competitor_2} onChange={setD("competitor_2")} placeholder="Competitor name" className={inputCls} /></div>
                                <div><Label className={labelCls}>Competitor 2 website</Label><Input type="url" data-testid="diag-comp2-web" value={details.competitor_2_website} onChange={setD("competitor_2_website")} placeholder="Competitor website link" className={inputCls} /></div>
                                <div><Label className={labelCls}>Competitor 3 name</Label><Input data-testid="diag-comp3" value={details.competitor_3} onChange={setD("competitor_3")} placeholder="Competitor name" className={inputCls} /></div>
                                <div><Label className={labelCls}>Competitor 3 website</Label><Input type="url" data-testid="diag-comp3-web" value={details.competitor_3_website} onChange={setD("competitor_3_website")} placeholder="Competitor website link" className={inputCls} /></div>
                            </div>
                        </div>

                        <div className="mt-8 flex items-center justify-between">
                            <button onClick={() => setStep(0)} className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-white"><ArrowLeft size={14} /> Back</button>
                            <button onClick={() => validateBusiness() && setStep(2)} data-testid="diag-next-1" className="gold-button inline-flex items-center gap-2">Continue <ArrowRight size={14} /></button>
                        </div>
                    </motion.div>
                )}

                {/* STEP 2 — NUMBERS & GOALS */}
                {step === 2 && (
                    <motion.div key="goals" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                        <h3 className="flex items-center gap-2 font-display text-2xl font-medium">
                            <Target size={20} className="text-gold-300" /> Numbers & goals
                        </h3>
                        <p className="mt-2 text-sm text-white/55">Help us understand your current business stage and growth target.</p>

                        <div className="mt-7 grid gap-7">
                            <div>
                                <Label className={labelCls}>Monthly Revenue Range</Label>
                                <Chips value={details.monthly_revenue} options={REVENUE} onChange={(v) => setVal("monthly_revenue", v)} testid="diag-rev" />
                            </div>
                            <div>
                                <Label className={labelCls}>How much growth are you looking for?</Label>
                                <Chips value={details.growth_target} options={GROWTH} onChange={(v) => setVal("growth_target", v)} testid="diag-growth" />
                            </div>
                            <div><Label className={labelCls}>Main business problem</Label><Textarea rows={3} data-testid="diag-problem" value={details.main_problem} onChange={setD("main_problem")} placeholder="What is the biggest business problem you want to solve?" className={areaCls} /></div>
                            <div><Label className={labelCls}>Support needed</Label><Textarea rows={3} data-testid="diag-support" value={details.support_needed} onChange={setD("support_needed")} placeholder="What support do you need right now? Example: branding, website, social media, ads, leads, sales, content, Google ranking." className={areaCls} /></div>

                            <div>
                                <Label className={labelCls}>Current marketing</Label>
                                <div className="mt-3 grid gap-3">
                                    <QuestionRow q="Are you currently doing social media management?" value={details.social_media_mgmt} onChange={(v) => setVal("social_media_mgmt", v)} testid="diag-q-social" />
                                    <QuestionRow q="Are you currently doing digital marketing or paid ads?" value={details.digital_marketing} onChange={(v) => setVal("digital_marketing", v)} testid="diag-q-ads" />
                                    <QuestionRow q="Have you worked with any design or digital marketing agency before?" value={details.worked_with_agency_before} onChange={(v) => setVal("worked_with_agency_before", v)} testid="diag-q-agency-before" />
                                    <QuestionRow q="Are you currently working with any agency?" value={details.currently_with_agency} onChange={(v) => setVal("currently_with_agency", v)} testid="diag-q-agency-now" />
                                </div>
                            </div>

                            <div>
                                <Label className={labelCls}>How old is your current website?</Label>
                                <Chips value={details.website_age} options={WEBSITE_AGE} onChange={(v) => setVal("website_age", v)} testid="diag-website-age" />
                            </div>

                            <div>
                                <Label className={labelCls}>Presence & online</Label>
                                <div className="mt-3 grid gap-3">
                                    <QuestionRow q="Do you have a mobile app for your business?" value={details.has_mobile_app} onChange={(v) => setVal("has_mobile_app", v)} testid="diag-q-app" />
                                    <QuestionRow q="Are your products or services already available online?" value={details.selling_online} onChange={(v) => setVal("selling_online", v)} testid="diag-q-online" />
                                </div>
                            </div>

                            <div className="grid gap-5 sm:grid-cols-2">
                                <div><Label className={labelCls}>Approximate margin %</Label><Input data-testid="diag-margin" value={details.margin_percentage} onChange={setD("margin_percentage")} placeholder="Example: 20%, 35%, 50%" className={inputCls} /></div>
                            </div>
                            <div><Label className={labelCls}>Who is your main audience or niche?</Label><Textarea rows={2} data-testid="diag-niche" value={details.niche_audience} onChange={setD("niche_audience")} placeholder="Example: working professionals, parents, students, business owners, women, doctors, retailers, restaurants, premium customers, local customers." className={areaCls} /></div>

                            <div>
                                <Label className={labelCls}>Recognition & PR</Label>
                                <div className="mt-3 grid gap-3">
                                    <QuestionRow q="Have you attended any business events, exhibitions or trade shows in the last 1–2 years?" value={details.attended_events} onChange={(v) => setVal("attended_events", v)} testid="diag-q-events" />
                                    <QuestionRow q="Have you received any awards or recognition in the last 5 years?" value={details.received_awards} onChange={(v) => setVal("received_awards", v)} testid="diag-q-awards" />
                                    <QuestionRow q="Have you done any PR, media coverage or press release activity?" value={details.pr_activity} onChange={(v) => setVal("pr_activity", v)} testid="diag-q-pr" />
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 flex items-center justify-between">
                            <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-white"><ArrowLeft size={14} /> Back</button>
                            <button onClick={() => validateGoals() && setStep(3)} data-testid="diag-next-2" className="gold-button inline-flex items-center gap-2">Continue <ArrowRight size={14} /></button>
                        </div>
                    </motion.div>
                )}

                {/* STEP 3 — UPLOAD ASSETS */}
                {step === 3 && (
                    <motion.div key="files" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                        <h3 className="flex items-center gap-2 font-display text-2xl font-medium">
                            <UploadCloud size={20} className="text-gold-300" /> Upload your assets
                        </h3>
                        <p className="mt-2 text-sm text-white/55">Upload the right files so we can understand your brand, products and business better.</p>

                        <div className="mt-7 grid gap-5">
                            {UPLOAD_SECTIONS.map((sec) => {
                                const secFiles = files.filter((f) => f.category === sec.key);
                                return (
                                    <div key={sec.key} className="rounded-lg border border-white/10 bg-white/[0.02] p-5" data-testid={`upload-section-${sec.key}`}>
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div>
                                                <p className="font-display text-base text-white/90">{sec.title}</p>
                                                <p className="mt-1 text-xs text-white/50">{sec.text}</p>
                                            </div>
                                            <span className="rounded-full bg-white/5 px-3 py-1 text-[10px] uppercase tracking-wide text-white/40">{sec.types} · {sec.hint}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => fileRefs.current[sec.key]?.click()}
                                            data-testid={`upload-trigger-${sec.key}`}
                                            className="mt-4 flex w-full items-center justify-center gap-3 rounded-md border border-dashed border-white/15 bg-white/[0.02] py-5 text-sm text-white/65 transition hover:border-gold-300/40 hover:bg-white/[0.04]"
                                        >
                                            {uploading === sec.key ? <Loader2 className="animate-spin text-gold-300" size={18} /> : <UploadCloud className="text-gold-300" size={18} />}
                                            {uploading === sec.key ? "Uploading…" : "Click to select files"}
                                        </button>
                                        <input
                                            ref={(el) => (fileRefs.current[sec.key] = el)}
                                            type="file"
                                            multiple
                                            accept={sec.accept}
                                            onChange={onPickFiles(sec.key, sec.limit)}
                                            data-testid={`upload-input-${sec.key}`}
                                            className="hidden"
                                        />
                                        {secFiles.length > 0 && (
                                            <ul className="mt-4 space-y-2" data-testid={`upload-list-${sec.key}`}>
                                                {secFiles.map((f) => (
                                                    <li key={f.id} className="flex items-center gap-3 rounded-md glass px-4 py-2.5 text-sm">
                                                        <FileText size={15} className="flex-none text-gold-300" />
                                                        <span className="flex-1 truncate text-white/80">{f.original_filename}</span>
                                                        <span className="text-xs text-white/40">{Math.round((f.size || 0) / 1024)} KB</span>
                                                        <button onClick={() => removeFile(f.id)} data-testid={`upload-remove-${f.id}`} className="text-white/40 hover:text-white"><X size={15} /></button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-8 flex items-center justify-between">
                            <button onClick={() => setStep(2)} className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-white"><ArrowLeft size={14} /> Back</button>
                            <button onClick={() => setStep(4)} data-testid="diag-next-3" className="gold-button inline-flex items-center gap-2">Continue <ArrowRight size={14} /></button>
                        </div>
                    </motion.div>
                )}

                {/* STEP 4 — REVIEW */}
                {step === 4 && (
                    <motion.div key="review" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                        <h3 className="font-display text-2xl font-medium">Review & submit</h3>
                        <p className="mt-2 text-sm text-white/55">Confirm your details below before you submit.</p>

                        <div className="mt-6 space-y-6" data-testid="diag-review">
                            {reviewGroups.map((g) => {
                                const rows = g.rows.filter(([, v]) => v);
                                if (!rows.length) return null;
                                return (
                                    <div key={g.title}>
                                        <p className="label-eyebrow mb-3">{g.title}</p>
                                        <div className="grid gap-2 text-sm">
                                            {rows.map(([k, v]) => (
                                                <div key={k} className="flex gap-4 rounded-md glass px-4 py-3">
                                                    <span className="w-40 flex-none text-white/45">{k}</span>
                                                    <span className="text-white/85 break-words">{v}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}

                            <div>
                                <p className="label-eyebrow mb-3">Uploads</p>
                                <div className="grid gap-2 text-sm">
                                    {UPLOAD_SECTIONS.map((sec) => {
                                        const count = files.filter((f) => f.category === sec.key).length;
                                        return (
                                            <div key={sec.key} className="flex gap-4 rounded-md glass px-4 py-3">
                                                <span className="w-40 flex-none text-white/45">{sec.title}</span>
                                                <span className="text-white/85">{count} file{count === 1 ? "" : "s"}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 flex items-center justify-between">
                            <button onClick={() => setStep(3)} className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-white"><ArrowLeft size={14} /> Back</button>
                            <button onClick={submit} disabled={submitting} data-testid="diag-submit" className="gold-button inline-flex items-center gap-2 disabled:opacity-50">
                                {submitting ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Submit Details
                            </button>
                        </div>
                    </motion.div>
                )}

                {/* STEP 5 — DONE / REPORT */}
                {step === 5 && (
                    <motion.div key="done" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="py-8 text-center" data-testid="diagnostic-done">
                        {reportFailed ? (
                            <div data-testid="report-delayed">
                                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-300/15 text-amber-300">
                                    <Mail size={26} />
                                </div>
                                <h3 className="mt-6 font-display text-3xl font-medium">Your report is taking a little longer</h3>
                                <p className="mx-auto mt-3 max-w-md text-sm text-white/60">
                                    Thank you — your payment and details are safe with us. Our analyst is
                                    finalising your Business Growth Diagnostic Report manually and it will be
                                    emailed to <span className="text-white">{details.email}</span> shortly. You can also reach us on
                                    WhatsApp if you need it urgently.
                                </p>
                                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                                    <a href="https://wa.me/919986616397" target="_blank" rel="noreferrer" data-testid="report-whatsapp-btn" className="gold-button inline-flex items-center gap-2">Message us on WhatsApp</a>
                                </div>
                            </div>
                        ) : !reportReady ? (
                            <div data-testid="report-preparing">
                                <div className="relative mx-auto flex h-20 w-20 items-center justify-center">
                                    <span className="absolute inset-0 animate-ping rounded-full bg-gold-300/20" />
                                    <span className="absolute inset-2 animate-pulse rounded-full bg-gold-300/10" />
                                    <Loader2 size={34} className="animate-spin text-gold-300" />
                                </div>
                                <h3 className="mt-7 font-display text-3xl font-medium">Thank you — we've received everything!</h3>
                                <p className="mx-auto mt-3 max-w-md text-sm text-white/60">
                                    Your business analysis report is being generated. This may take
                                    around <span className="text-gold-300">5 to 8 minutes</span> —
                                    please keep this window open while we work on your report.
                                </p>
                                <div className="mx-auto mt-7 flex max-w-sm flex-col gap-2 text-left">
                                    {["Checking your website & on-page basics", "Reviewing your brand & uploads", "Scanning competitors & building your roadmap"].map((s) => (
                                        <div key={s} className="flex items-center gap-3 rounded-md glass px-4 py-2.5 text-sm text-white/70">
                                            <Loader2 size={13} className="animate-spin text-gold-300" /> {s}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div data-testid="report-ready">
                                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold-300/15 text-gold-300">
                                    <PartyPopper size={28} />
                                </div>
                                <h3 className="mt-6 font-display text-3xl font-medium">{adminMode ? "Analysis ready!" : "Your report is ready!"}</h3>
                                <p className="mx-auto mt-3 max-w-md text-sm text-white/60">
                                    {adminMode ? (
                                        <>The Business Diagnostic Report for <span className="text-white">{details.business_name || details.full_name}</span> is ready. Download it, or email it directly to <span className="text-white">{details.email}</span>.</>
                                    ) : (
                                        <>Your Business Diagnostic Report has been prepared and a copy is on its way to <span className="text-white">{details.email}</span>. Download your copy below.</>
                                    )}
                                </p>
                                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                                    <button onClick={downloadReport} disabled={downloading} data-testid="download-report-btn" className="gold-button inline-flex items-center gap-2 disabled:opacity-50">
                                        {downloading ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />} Download Report
                                    </button>
                                    {adminMode && (
                                        <button onClick={sendEmail} disabled={sendingEmail} data-testid="send-report-btn" className="ghost-button inline-flex items-center gap-2 disabled:opacity-50">
                                            {sendingEmail ? <Loader2 className="animate-spin" size={14} /> : <Mail size={14} />} Send Email to Client
                                        </button>
                                    )}
                                </div>
                                {!adminMode && (
                                    <p className="mt-5 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-white/45">
                                        <Mail size={12} /> Also emailed to you
                                    </p>
                                )}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
